import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { useFlushOnUnmount } from "#app/lib/hooks/use-flush-on-unmount";
import {
  Box,
  Text,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Checkbox,
  DatePicker,
  Select,
  SelectTrigger,
  SelectPopup,
  SelectItem,
  SelectValue,
  Icon,
  cn,
  pickImageFile,
  pickImageFiles,
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  useIsMobile,
  type EntitySelectorConfig,
  type EntitySelection,
} from "@salestrak/ui";
import { AddressPropertyInput } from "#app/components/issue-view/address-property-input";
import { UserSelector } from "#app/components/entity-selectors";
import { DeletePhotoDialog } from "#app/components/custom-fields/delete-photo-dialog";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import {
  AddMediaTile,
  FIELD_MEDIA_TILE_SIZE,
  RemovableMediaTile,
  UploadingMediaTile,
} from "#app/components/media/media-tiles";
import { usePendingMediaUploads } from "#app/lib/hooks/use-pending-media-uploads";
import { stripHtmlToText } from "@salestrak/utilities";
import {
  normalizeRichTextValue,
  isRichTextEmpty,
} from "#app/lib/utils/rich-text-value";
import type { CustomFieldDef } from "./types";
import { getFieldTypeDescriptor } from "#app/components/fields/field-type-descriptors";
import { defaultCountry } from "@salestrak/custom-fields";
import {
  typedCountryCodes,
  countryCodeMap,
  countryFlag,
  getPhoneCountryIso,
  getExamplePhoneForCountry,
  formatDateValue,
  toDateValue,
  isMultipleField,
  compositeShellProps,
  NONE_OPTION_LABEL,
  NONE_OPTION_VALUE,
  type FieldSurface,
  type FieldControlSize,
} from "#app/lib/custom-field-utils";
import {
  CUSTOM_FIELD_MAX_LENGTHS,
  currencySymbol,
  getDefaultValueByType,
} from "@salestrak/custom-fields";
import * as flagSvgs from "country-flag-icons/string/3x2";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type FieldRendererProps = Omittable<{
  field: CustomFieldDef;
  /** Current value (already run through dataToField → fieldValue) */
  value: unknown;
  /** Fires with the raw form value — the parent wraps this with fieldDto */
  onChangeValue: (value: unknown) => void;
  /**
   * Photos-field only. When set (view mode, issue exists), a landed upload /
   * removed tile commits as a single-photo DELTA instead of emitting the whole
   * array through `onChangeValue` — concurrent uploads then can't clobber each
   * other. Absent in create mode (no issue yet), where the renderer falls back
   * to accumulating the full array into the draft via `onChangeValue`.
   */
  onAddPhoto?: (photoId: string) => void | Promise<void>;
  onRemovePhoto?: (photoId: string) => void | Promise<void>;
  /** Whether the field is currently in its active editing state. */
  isActive?: boolean;
  /** Called when the field should exit its active editing state. */
  onDeactivate?: () => void;
  /** Autofocus the primary input when the field enters editing. */
  autoFocus?: boolean;
  /** Whether the row is currently in an invalid state (drives red border). */
  invalid?: boolean;
  /**
   * Visual chrome for the primary input. `"table"` (default) is the
   * borderless inline-cell look used in the issue view; `"default"` is the
   * bordered, input-tinted (bg-input/30) form-field look used in the
   * custom-field create/edit form.
   */
  surface?: FieldSurface;
  /**
   * Control height for the primary input. Omitted → each control's own default
   * (the inline/table look). Set (e.g. `"lg"` = 40px) to align with sibling
   * form controls — used by the automation condition builder.
   */
  size?: FieldControlSize;
}>;

// Controls ship one filled look, so each surface is a className recipe.
// "default" keeps stock chrome; "ghost" drops the fill; "table" strips the
// control down to a borderless inline cell.
// The ring is a separate box-shadow layer from `shadow`, so `shadow-none` does
// not touch it — without the `ring-0` rungs a focused table control still draws
// its 3px focus halo inside the editing cell, which already marks focus with its
// own border. Both spellings are needed: Input rings from the wrapper
// (`has-focus-visible:`), Select from the trigger itself (`focus-visible:`).
const TABLE_CONTROL_CLASS =
  "rounded-none border-0 bg-transparent shadow-none before:hidden ring-0 focus-visible:ring-0 has-focus-visible:ring-0 dark:bg-transparent";

function inputClassFor(surface: FieldSurface): string | undefined {
  if (surface === "default") return undefined;
  if (surface === "ghost") return "bg-transparent dark:bg-transparent";
  return TABLE_CONTROL_CLASS;
}

function selectClassFor(surface: FieldSurface): string | undefined {
  if (surface === "default") return undefined;
  if (surface === "ghost") return "bg-transparent dark:bg-transparent";
  return TABLE_CONTROL_CLASS;
}

// The app's field-control scale ("xs"–"xl") clamps onto the narrower control
// scale, same as `toEntityTriggerSize` does for selectors.
function toControlSize(
  size: FieldControlSize | undefined,
): "sm" | "default" | "lg" | undefined {
  switch (size) {
    case "xs":
    case "sm":
      return "sm";
    case "lg":
    case "xl":
      return "lg";
    case "default":
      return "default";
    default:
      return undefined;
  }
}

type FieldRenderer = React.ComponentType<FieldRendererProps>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// On-screen keyboard hint per field type (email also sets the field `type`).
const INPUT_MODE_MAP: Record<string, "text" | "numeric"> = {
  text: "text",
  number: "numeric",
};

// ---------------------------------------------------------------------------
// Country flag rendering
// ---------------------------------------------------------------------------

const FLAG_ASPECT_RATIO = 2 / 3;
const flagSvgMap = flagSvgs as Record<string, string>;

function getFlagSvg(iso: string): string | null {
  return flagSvgMap[iso] ?? null;
}

// The source flag markup carries only a viewBox, so inject explicit
// width/height onto its root element for a fixed pixel size. Trusted static
// markup from `country-flag-icons`, never user input.
const FLAG_ROOT_TAG_RE = /^<svg\b/;
function sizedFlagSvg(svg: string, width: number, height: number): string {
  return svg.replace(FLAG_ROOT_TAG_RE, (m) => `${m} width="${width}" height="${height}"`);
}

export function CountryFlagImage({ iso, size = 20 }: { iso: string; size?: number }) {
  const svg = getFlagSvg(iso);
  const h = Math.round(size * FLAG_ASPECT_RATIO);
  // Render the static SVG string via `innerHTML` on the Box's DOM node — Box
  // forwards to a host element, keeping the layout primitive rather than
  // emitting a raw host tag from JSX.
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.innerHTML = svg ? sizedFlagSvg(svg, size, h) : "";
  }, [svg, size, h]);

  if (!svg) return <Text size="sm">{countryFlag(iso)}</Text>;
  return (
    <Box ref={ref} width={size} height={h} overflow="hidden" shrink={false} />
  );
}


function scheduleFieldDeactivate(
  fieldRoot: HTMLElement | null,
  onDeactivate?: () => void,
  allowPortalFocus: boolean | (() => boolean) = false,
) {
  if (!onDeactivate) return;

  requestAnimationFrame(() => {
    const activeElement = document.activeElement as HTMLElement | null;

    if (fieldRoot?.contains(activeElement)) {
      return;
    }

    const portalFocusAllowed =
      typeof allowPortalFocus === "function"
        ? allowPortalFocus()
        : allowPortalFocus;

    if (
      portalFocusAllowed &&
      activeElement?.closest(
        "[data-side], [role='dialog'], [role='listbox'], [data-slot$='-popup']",
      )
    ) {
      return;
    }

    onDeactivate();
  });
}

// ---------------------------------------------------------------------------
// Text / Number / Email  (shared renderer, varies keyboardType)
// ---------------------------------------------------------------------------

function TextFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  surface = "table",
  size,
}: FieldRendererProps) {
  const textRef = useRef(String(value ?? ""));
  const emitTextValue = useCallback(
    (text: string) => {
      const nextValue =
        field.type === "number" && text.trim() === "" ? null : text;
      onChangeValue(nextValue);
    },
    [field.type, onChangeValue],
  );
  const markFlushed = useFlushOnUnmount(textRef, emitTextValue);

  return (
    <Input
      defaultValue={textRef.current}
      onChange={(event) => {
        textRef.current = event.target.value;
      }}
      onBlur={() => {
        markFlushed();
        emitTextValue(textRef.current);
        onDeactivate?.();
      }}
      placeholder={undefined}
      inputMode={INPUT_MODE_MAP[field.type] ?? "text"}
      className={inputClassFor(surface)}
      size={toControlSize(size)}
      maxLength={CUSTOM_FIELD_MAX_LENGTHS[field.type]}
      autoFocus={autoFocus}
    />
  );
}

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

function EmailFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
}: FieldRendererProps) {
  const textRef = useRef(String(value ?? ""));
  const markFlushed = useFlushOnUnmount(textRef, onChangeValue);

  return (
    <Box fill="flex">
      <Input
        defaultValue={textRef.current}
        onChange={(event) => {
          textRef.current = event.target.value;
        }}
        onBlur={() => {
          markFlushed();
          onChangeValue(textRef.current);
          onDeactivate?.();
        }}
        placeholder={"Email address"}
        type="email"
        className={inputClassFor(surface)}
        size={toControlSize(size)}
        maxLength={CUSTOM_FIELD_MAX_LENGTHS.email}
        aria-invalid={invalid}
        autoFocus={autoFocus}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Paragraph
// ---------------------------------------------------------------------------

function ParagraphFieldRenderer({
  field,
  value,
  onChangeValue,
  autoFocus,
}: FieldRendererProps) {
  // Initial seed only. onUpdate fires only on real edits, so opening a legacy
  // plaintext record never dirties/persists it.
  const initialHtml = useMemo(() => normalizeRichTextValue(value), [value]);
  const htmlRef = useRef(initialHtml);
  const markFlushed = useFlushOnUnmount(htmlRef, onChangeValue);

  return (
    <Box>
      <RichTextEditor
        compact
        mentions
        autoFocus={autoFocus}
        content={initialHtml}
        placeholder={"Write"}
        shortcutHints
        contentPaddingX="sm"
        contentPaddingY="sm"
        onUpdate={(html) => {
          htmlRef.current = html;
          markFlushed();
          onChangeValue(html);
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Select (dropdown) — single or multi, driven by config.multiple. Values are
// always an array of option values; single-mode caps the array at one.
// ---------------------------------------------------------------------------

function SelectRenderer(props: FieldRendererProps) {
  return isMultipleField(props.field.config) ? (
    <MultiSelectRenderer {...props} />
  ) : (
    <SingleSelectRenderer {...props} />
  );
}

function SingleSelectRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  isActive,
  surface = "table",
  size,
}: FieldRendererProps) {
  const options = useMemo(
    () =>
      (field.config?.options ?? []).map((opt) => ({
        value: opt.value ?? opt.label ?? "",
        label: opt.label ?? opt.value ?? "",
      })),
    [field.config?.options],
  );

  // Value is an array; single-mode reads the first element.
  const currentValue = Array.isArray(value) ? (value[0] ?? null) : value ?? null;
  const selectedOption = useMemo(() => {
    if (currentValue == null) return undefined;
    const str = String(currentValue);
    return options.find((o) => o.value === str);
  }, [currentValue, options]);
  const didSelectRef = useRef(false);
  // Base UI's Select exposes no imperative `open()` handle, so "activate opens
  // the dropdown" is controlled open state.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isActive) {
      requestAnimationFrame(() => setOpen(true));
    }
  }, [isActive]);

  const items = useMemo(
    () => [{ value: NONE_OPTION_VALUE, label: NONE_OPTION_LABEL }, ...options],
    [options],
  );

  return (
    <Box fill="flex">
      <Select
        items={items}
        value={selectedOption?.value ?? null}
        open={open}
        onOpenChange={(nextOpen: boolean) => {
          setOpen(nextOpen);
          if (nextOpen) return;

          requestAnimationFrame(() => {
            if (didSelectRef.current) {
              didSelectRef.current = false;
              return;
            }
            onDeactivate?.();
          });
        }}
        onValueChange={(next: string | null) => {
          if (next) {
            didSelectRef.current = true;
            onChangeValue(next === NONE_OPTION_VALUE ? [] : [next]);
            onDeactivate?.();
          }
        }}
      >
        <SelectTrigger
          className={selectClassFor(surface)}
          size={toControlSize(size)}
        >
          <SelectValue placeholder={"Select..."} />
        </SelectTrigger>
        <SelectPopup>
          <SelectItem value={NONE_OPTION_VALUE}>{NONE_OPTION_LABEL}</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </Box>
  );
}

function MultiSelectRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  isActive,
  surface = "table",
  size,
}: FieldRendererProps) {
  const options = useMemo(
    () =>
      (field.config?.options ?? []).map((opt) => ({
        value: opt.value ?? opt.label ?? "",
        label: opt.label ?? opt.value ?? "",
      })),
    [field.config?.options],
  );

  const currentValues = useMemo(
    () => (Array.isArray(value) ? (value as string[]) : []),
    [value],
  );
  // Same story as the single select: no imperative open handle in Base UI.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isActive) {
      requestAnimationFrame(() => setOpen(true));
    }
  }, [isActive]);

  const items = useMemo(
    () => [{ value: NONE_OPTION_VALUE, label: NONE_OPTION_LABEL }, ...options],
    [options],
  );

  // DESIGN-TUNABLE: Base UI's Select is not a combobox and has no search slot,
  // so the option list is plain and the trigger carries no item-count summary —
  // `SelectValue` renders the joined labels.
  return (
    <Box fill="flex">
      <Select
        multiple
        items={items}
        value={currentValues}
        open={open}
        onOpenChange={(nextOpen: boolean) => {
          setOpen(nextOpen);
          if (!nextOpen) onDeactivate?.();
        }}
        onValueChange={(vals: string[]) =>
          onChangeValue(vals.includes(NONE_OPTION_VALUE) ? [] : vals)
        }
      >
        <SelectTrigger
          className={selectClassFor(surface)}
          size={toControlSize(size)}
        >
          <SelectValue placeholder={"Select..."} />
        </SelectTrigger>
        <SelectPopup>
          <SelectItem value={NONE_OPTION_VALUE}>{NONE_OPTION_LABEL}</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Date / DateTime / Time
// ---------------------------------------------------------------------------

const DATE_PLACEHOLDER_MAP: Record<string, string> = {
  date: "date",
  dateTime: "date & time",
  time: "time",
};

function DateFieldRenderer({
  field,
  value,
  onChangeValue,
  surface = "table",
  size,
}: FieldRendererProps) {
  const dateValue = useMemo(() => toDateValue(value), [value]);

  const placeholder =
    `Select ${DATE_PLACEHOLDER_MAP[field.type] ?? field.type}...`;

  // The library picker owns the calendar and the typed time field, so the
  // browser's own control is never reached.
  return (
    <Box fill="flex">
      <DatePicker
        ariaLabel={placeholder}
        mode={getFieldTypeDescriptor(field.type).pickerMode ?? "date"}
        onValueChange={onChangeValue}
        placeholder={placeholder}
        size={toControlSize(size)}
        value={dateValue}
        variant={surface === "ghost" ? "ghost" : "outline"}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

function MoneyFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
}: FieldRendererProps) {
  const symbol = currencySymbol(field.config?.currency);
  const textRef = useRef(String(value ?? ""));
  const inputRef = useRef<React.ComponentRef<typeof Input>>(null);
  const emitMoneyValue = useCallback(
    (text: string) => {
      onChangeValue(text.trim() === "" ? null : text);
    },
    [onChangeValue],
  );
  const markFlushed = useFlushOnUnmount(textRef, emitMoneyValue);

  return (
    <Box
      as="div"
      direction="row"
      align="center"
      fill="flex"
      paddingX="md"
      onClick={() => inputRef.current?.focus()}
      cursor="text"
      {...compositeShellProps(surface, size)}
    >
      <Text size="sm">{symbol}</Text>
      <Box fill="flex">
        <Input
          ref={inputRef}
          defaultValue={textRef.current}
          onChange={(event) => {
            textRef.current = event.target.value;
          }}
          onBlur={() => {
            markFlushed();
            emitMoneyValue(textRef.current);
            onDeactivate?.();
          }}
          placeholder="0.00"
          inputMode="numeric"
          // Class recipes: strip the control chrome, then zero the leading
          // inset so the value hugs the currency symbol.
          className={cn(TABLE_CONTROL_CLASS, "[&_input]:ps-0")}
          aria-invalid={invalid}
          autoFocus={autoFocus}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Phone  (country code picker + input)
// ---------------------------------------------------------------------------

const countryPool = {
  id: "countries",
  label: "Countries",
  items: typedCountryCodes.map((c) => ({
    id: c.country,
    title: `${c.code} ${c.name}`,
    keywords: [c.code, c.name, c.country],
    avatar: {
      render: () => <CountryFlagImage iso={c.country} size={22} />,
    },
  })),
};

const countryPoolItemsById: Record<
  string,
  (typeof countryPool.items)[number]
> = Object.fromEntries(countryPool.items.map((item) => [item.id, item]));

const countryPickerConfig: EntitySelectorConfig = {
  pools: [countryPool],
  maxSelections: 1,
  searchPlaceholder: "Search countries...",
  emptyMessage: "No countries found",
};

function PhoneFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
}: FieldRendererProps) {
  const phoneObj =
    typeof value === "object" && value !== null
      ? (value as { countryCode?: string; value?: string; country?: string })
      : null;

  const [selectedCountryKey, setSelectedCountryKey] = useState(
    () => getPhoneCountryIso(value) || defaultCountry,
  );
  const [phoneText, setPhoneText] = useState(() => {
    if (phoneObj?.value != null) return phoneObj.value;
    return value != null ? String(value) : "";
  });
  const [isCountrySelectorOpen, setIsCountrySelectorOpen] = useState(false);

  const selectedCountry = countryCodeMap[selectedCountryKey];
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dirtyRef = useRef(false);
  const phoneTextRef = useRef(phoneText);
  phoneTextRef.current = phoneText;
  const selectedCountryKeyRef = useRef(selectedCountryKey);
  selectedCountryKeyRef.current = selectedCountryKey;
  const countrySelectorOpenRef = useRef(isCountrySelectorOpen);
  countrySelectorOpenRef.current = isCountrySelectorOpen;

  const emitFullPhoneNumber = useCallback(
    (phone: string, countryKey: string) => {
      const country = countryCodeMap[countryKey];
      onChangeValue({ value: phone, countryCode: country?.code ?? "+1", country: countryKey });
    },
    [onChangeValue],
  );

  const emitRef = useRef(emitFullPhoneNumber);
  emitRef.current = emitFullPhoneNumber;

  // Flush unsaved phone value on unmount. Always emit raw — the parent's
  // validation gate decides whether to commit; silently dropping invalid
  // input here was a data-loss bug (user thought they saved).
  useEffect(() => {
    return () => {
      if (dirtyRef.current) {
        emitRef.current(phoneTextRef.current, selectedCountryKeyRef.current);
      }
    };
  }, []);

  const handlePhoneChange = useCallback(
    (text: string) => {
      dirtyRef.current = true;
      setPhoneText(text);
    },
    [],
  );

  const handlePhoneBlur = useCallback(() => {
    dirtyRef.current = false;
    emitFullPhoneNumber(phoneText, selectedCountryKey);
    scheduleFieldDeactivate(
      rootRef.current as HTMLElement | null,
      onDeactivate,
      () => countrySelectorOpenRef.current,
    );
  }, [phoneText, selectedCountryKey, emitFullPhoneNumber, onDeactivate]);

  const handleCountrySelect = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (!selected) return;
      const country = countryCodeMap[selected.id];
      if (country) {
        setSelectedCountryKey(selected.id);
        dirtyRef.current = false;
        emitFullPhoneNumber(phoneText, selected.id);
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }
    },
    [phoneText, emitFullPhoneNumber],
  );

  const handleCountrySelectorOpenChange = useCallback(
    (nextOpen: boolean) => {
      setIsCountrySelectorOpen(nextOpen);

      if (nextOpen) return;

      requestAnimationFrame(() => {
        const fieldRoot = rootRef.current as HTMLElement | null;
        const activeElement = document.activeElement as HTMLElement | null;

        if (fieldRoot?.contains(activeElement)) {
          inputRef.current?.focus();
          return;
        }

        scheduleFieldDeactivate(fieldRoot, onDeactivate);
      });
    },
    [onDeactivate],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const entity = countryPoolItemsById[selectedCountryKey];
    if (!entity) return [];
    return [{ id: selectedCountryKey, poolId: countryPool.id, entity }];
  }, [selectedCountryKey]);

  return (
    <Box
      ref={rootRef}
      direction="row"
      align="center"
      fill="flex"
      {...compositeShellProps(surface, size)}
    >
      <EntitySelector
        config={countryPickerConfig}
        value={selections}
        onValueChange={handleCountrySelect}
        onOpenChange={handleCountrySelectorOpenChange}
      >
        <EntitySelectorTrigger
          render={
            <Box
              as="button"
              direction="row"
              align="center"
              fill="height"
              gap="xs"
              paddingX="xs"
              paddingLeft="sm"
              shrink={false}
              cursor="pointer"
            >
              <CountryFlagImage iso={selectedCountryKey} size={20} />
              <Text size="sm">
                {selectedCountry?.code ?? "+1"}
              </Text>
              <Icon name="chevron-down" size="md" />
            </Box>
          }
        />
        <EntitySelectorContent align="start" size="sm">
          <EntitySelectorInput />
          <EntitySelectorPool value={countryPool.id} />
          <EntitySelectorEmpty>No countries found</EntitySelectorEmpty>
        </EntitySelectorContent>
      </EntitySelector>
      <Box fill="flex" shrink minWidth={60}>
        <Input
          ref={inputRef}
          value={phoneText}
          onChange={(event) => handlePhoneChange(event.target.value)}
          onBlur={handlePhoneBlur}
          placeholder={getExamplePhoneForCountry(selectedCountryKey)}
          type="tel"
          className={cn(TABLE_CONTROL_CLASS, "[&_input]:ps-0")}
          maxLength={CUSTOM_FIELD_MAX_LENGTHS.phone}
          aria-invalid={invalid}
          autoFocus={autoFocus}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// URL
// ---------------------------------------------------------------------------

function UrlFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
}: FieldRendererProps) {
  const textRef = useRef(String(value ?? "").replace(/^https?:\/\//, ""));
  const inputRef = useRef<React.ComponentRef<typeof Input>>(null);
  const emitUrlValue = useCallback(
    (rawValue: string) => {
      const normalizedValue = rawValue.trim().replace(/^https?:\/\//, "");
      onChangeValue(normalizedValue ? `https://${normalizedValue}` : null);
    },
    [onChangeValue],
  );
  const markFlushed = useFlushOnUnmount(textRef, emitUrlValue);

  return (
    <Box
      as="div"
      direction="row"
      align="stretch"
      fill="flex"
      paddingLeft="sm"
      onClick={() => inputRef.current?.focus()}
      cursor="text"
      {...compositeShellProps(surface, size)}
    >
      <Box justify="center" paddingLeft="sm">
        <Text size="sm" variant="muted">
          https://
        </Text>
      </Box>
      <Box fill="flex">
        <Input
          ref={inputRef}
          defaultValue={textRef.current}
          onChange={(event) => {
            textRef.current = event.target.value.replace(/^https?:\/\//, "");
          }}
          onBlur={() => {
            markFlushed();
            emitUrlValue(textRef.current);
            onDeactivate?.();
          }}
          placeholder={"example.com"}
          type="url"
          className={cn(TABLE_CONTROL_CLASS, "[&_input]:ps-0")}
          maxLength={CUSTOM_FIELD_MAX_LENGTHS.url}
          aria-invalid={invalid}
          autoFocus={autoFocus}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Address  (Google Places autocomplete via AddressPropertyInput)
// ---------------------------------------------------------------------------

function AddressFieldRenderer({
  value,
  onChangeValue,
  onDeactivate,
  isActive,
  surface = "table",
}: FieldRendererProps) {
  const wholeAddress =
    typeof value === "object" && value !== null && "wholeAddress" in value
      ? (value as { wholeAddress?: string }).wholeAddress
      : null;

  const handleSelect = useCallback(
    (address: string) => {
      onChangeValue({
        ...(typeof value === "object" ? value : {}),
        wholeAddress: address,
      });
      onDeactivate?.();
    },
    [value, onChangeValue, onDeactivate],
  );

  const handleClear = useCallback(() => {
    onChangeValue({});
  }, [onChangeValue]);

  return (
    <AddressPropertyInput
      variant={surface}
      wholeAddress={wholeAddress}
      onSelect={handleSelect}
      onClear={handleClear}
      autoOpen={isActive}
      onOpenChange={(open) => {
        if (!open) onDeactivate?.();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// SingleMember / MultiMember  (display-only for now — EntitySelector needs
// workspace user data to be wired in)
// ---------------------------------------------------------------------------

function MemberFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  size,
}: FieldRendererProps) {
  // Cardinality is config-driven. Values are always an id array; single-mode
  // just caps the selection at one element. Member fields are members-only.
  const isSingle = !isMultipleField(field.config);
  const maxSelections = isSingle ? 1 : undefined;

  return (
    <UserSelector
      size={size}
      value={value as string | string[] | null}
      onOpenChange={(open) => {
        if (!open) onDeactivate?.();
      }}
      onValueChange={(ids) => {
        onChangeValue(ids);
        if (isSingle) onDeactivate?.();
      }}
      maxSelections={maxSelections}
      placeholder="Select member..."
    />
  );
}

// ---------------------------------------------------------------------------
// Checkbox  (boolean — toggles directly, no edit mode)
// ---------------------------------------------------------------------------

function CheckboxFieldRenderer({
  value,
  onChangeValue,
  onDeactivate,
}: FieldRendererProps) {
  return (
    <Checkbox
      checked={value === true}
      onCheckedChange={(checked) => {
        onChangeValue(checked);
        onDeactivate?.();
      }}
    />
  );
}

export function isCustomFieldValueEmpty(
  field: CustomFieldDef,
  value: unknown,
): boolean {
  // Checkbox is tri-state: only `null`/`undefined` is empty. A stored
  // `false` is a deliberate "no" — keep it BEFORE the defaultVal guard
  // below, which would otherwise treat `false` as empty.
  if (field.type === "checkbox") return value == null;

  const defaultVal = getDefaultValueByType(field.type);
  if (value == null || value === defaultVal) return true;

  switch (field.type) {
    case "paragraph":
      // Stored as HTML — empty when there's no visible text.
      return isRichTextEmpty(value);
    case "select":
    case "member":
    case "photos":
      return !Array.isArray(value) || value.length === 0;
    case "date":
    case "dateTime":
    case "time":
      return toDateValue(value) == null;
    case "phone": {
      if (typeof value === "object" && value !== null) {
        const phone = (value as { value?: unknown }).value;
        return phone == null || String(phone).trim() === "";
      }
      return value == null || String(value).trim() === "";
    }
    case "address": {
      if (typeof value === "object" && value !== null) {
        const wholeAddress = (value as { wholeAddress?: unknown }).wholeAddress;
        return wholeAddress == null || String(wholeAddress).trim() === "";
      }
      return value == null || String(value).trim() === "";
    }
    case "url": {
      if (value == null) return true;
      return String(value).replace(/^https?:\/\//, "").trim() === "";
    }
    default:
      return value == null || (typeof value === "string" && value.trim() === "");
  }
}

export function getCustomFieldDisplayValue(
  field: CustomFieldDef,
  value: unknown,
): string {
  if (isCustomFieldValueEmpty(field, value)) return NONE_OPTION_LABEL;

  switch (field.type) {
    case "checkbox":
      return value ? "Yes" : "No";
    case "select": {
      const selectedValues = Array.isArray(value) ? value.map(String) : [];
      const labels = selectedValues.map((selectedValue) => {
        const option = field.config?.options?.find(
          (opt) => (opt?.value ?? opt?.label ?? "") === selectedValue,
        );
        return option?.label ?? selectedValue;
      });
      return labels.join(", ");
    }
    case "date":
    case "dateTime":
    case "time":
      return formatDateValue(value, field.type);
    case "money": {
      if (typeof value === "number" && Number.isNaN(value)) return NONE_OPTION_LABEL;
      const symbol = currencySymbol(field.config?.currency);
      return `${symbol}${String(value)}`;
    }
    case "phone": {
      if (typeof value === "object" && value !== null) {
        const phoneValue = (value as { value?: unknown }).value;
        if (phoneValue != null && String(phoneValue).trim() !== "") return String(phoneValue);
        return NONE_OPTION_LABEL;
      }
      return String(value);
    }
    case "address":
      if (typeof value === "object" && value !== null && "wholeAddress" in value) {
        return String((value as { wholeAddress?: unknown }).wholeAddress ?? NONE_OPTION_LABEL);
      }
      return String(value);
    case "member": {
      const count = Array.isArray(value) ? value.length : 0;
      return `${count} member${count === 1 ? "" : "s"}`;
    }
    case "photos": {
      const count = Array.isArray(value) ? value.length : 0;
      return `${count} photo${count === 1 ? "" : "s"}`;
    }
    case "url":
      return String(value).replace(/^https?:\/\//, "");
    case "paragraph":
      return stripHtmlToText(String(value));
    default:
      return String(value);
  }
}

// ---------------------------------------------------------------------------
// Renderer map
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Photos — array of permission-gated photo ids. Tiles resolve through the
// authenticated content route; add prompts for files and uploads as media
// (linked to the issue server-side when the value persists). Single mode
// replaces the photo instead of growing the list.
// ---------------------------------------------------------------------------

function PhotosFieldRenderer({
  field,
  value,
  onChangeValue,
  onAddPhoto,
  onRemovePhoto,
  surface = "table",
}: FieldRendererProps) {
  const isMobile = useIsMobile();
  // Mobile-only: the tiny always-visible remove "×" is easy to fumble, so a tap
  // opens a confirm sheet instead of deleting. Holds the id awaiting confirm.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const multiple = isMultipleField(field.config);
  const ids = useMemo(
    () =>
      Array.isArray(value)
        ? value.filter((v): v is string => typeof v === "string")
        : [],
    [value],
  );
  // View mode (issue exists) commits each landed photo as a single-photo delta
  // — `onAddPhoto`/`onRemovePhoto` own an atomic, commutative server write, so
  // concurrent uploads can't clobber one another. Create mode has no issue yet,
  // so it falls back to accumulating the full array into the draft. Adds are
  // async (prompt + upload); two render-time-composed adds would overwrite, so
  // the fallback composes from the latest EMITTED list, synced back whenever
  // the committed value changes.
  const delta = !!onAddPhoto && !!onRemovePhoto;
  const latestIds = useRef(ids);
  useEffect(() => {
    latestIds.current = ids;
  }, [ids]);

  const { pending, startUploads, cancel } = usePendingMediaUploads({
    onUploaded: ({ photoId }) => {
      if (delta) {
        // Single mode replaces: drop the prior photo before adding the new one.
        if (!multiple && ids[0] && ids[0] !== photoId) onRemovePhoto(ids[0]);
        onAddPhoto(photoId);
        return;
      }
      const next = multiple ? [...latestIds.current, photoId] : [photoId];
      latestIds.current = next;
      onChangeValue(next);
    },
  });

  const handleAdd = async () => {
    const files = multiple
      ? await pickImageFiles()
      : await pickImageFile().then((f) => (f ? [f] : []));
    if (!files.length) return;
    // Snapshot the photos already on the field BEFORE the batch (at click time
    // nothing is in flight, so the cache is accurate here — unlike at batch
    // end, where the React `ids` prop lags the synchronous optimistic writes).
    const base = delta && multiple ? ids : null;
    // startUploads commits in pick order and returns the committed photos in
    // that order. The per-photo delta adds race to the server (completion
    // order), so once the batch settles we rewrite the field ONCE in pick
    // order to lock it in for reloads. Single post-batch wholesale write built
    // from this authoritative list (never from the lagging cache), with a set
    // identical to what the deltas persisted — so it reorders without ever
    // dropping a photo.
    const committed = await startUploads(files);
    if (base) {
      const ordered = [...base, ...committed.map((c) => c.photoId)].filter(
        (id, i, arr) => arr.indexOf(id) === i,
      );
      onChangeValue(ordered);
    }
  };

  const handleRemove = (photoId: string) => {
    if (delta) {
      onRemovePhoto(photoId);
      return;
    }
    const next = latestIds.current.filter((id) => id !== photoId);
    latestIds.current = next;
    onChangeValue(next);
  };

  const tiles = (
    <Box
      fill="flex"
      direction="row"
      wrap
      align="center"
      gap="sm"
      paddingY="xs"
      paddingX={surface === "table" ? "md" : "xs"}
    >
      {ids.map((photoId, index) => (
        <RemovableMediaTile
          key={`${photoId}-${index}`}
          photoId={photoId}
          // Mobile confirms before deleting (easy-to-mistap × on a 32px tile);
          // desktop's hover-revealed × deletes directly.
          onRemove={isMobile ? setConfirmDeleteId : handleRemove}
          size={FIELD_MEDIA_TILE_SIZE}
        />
      ))}
      {pending.map((p) => (
        <UploadingMediaTile
          key={p.tempId}
          uri={p.uri}
          progress={p.progress}
          onCancel={() => cancel(p.tempId)}
          size={FIELD_MEDIA_TILE_SIZE}
        />
      ))}
      {/* Multiple: always allow adding more. Single: only when nothing is set
          and nothing is uploading — the progress tile stands in for Add. */}
      {multiple || (ids.length === 0 && pending.length === 0) ? (
        <AddMediaTile onClick={handleAdd} size={FIELD_MEDIA_TILE_SIZE} />
      ) : null}
    </Box>
  );

  if (!isMobile) return tiles;

  return (
    <>
      {tiles}
      <DeletePhotoDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmDeleteId(null);
        }}
        onConfirm={() => {
          if (confirmDeleteId) handleRemove(confirmDeleteId);
        }}
      />
    </>
  );
}

export const FIELD_RENDERERS: Record<string, FieldRenderer> = {
  text: TextFieldRenderer,
  number: TextFieldRenderer,
  email: EmailFieldRenderer,
  paragraph: ParagraphFieldRenderer,
  select: SelectRenderer,
  date: DateFieldRenderer,
  dateTime: DateFieldRenderer,
  time: DateFieldRenderer,
  member: MemberFieldRenderer,
  checkbox: CheckboxFieldRenderer,
  money: MoneyFieldRenderer,
  phone: PhoneFieldRenderer,
  url: UrlFieldRenderer,
  address: AddressFieldRenderer,
  photos: PhotosFieldRenderer,
};

// ---------------------------------------------------------------------------
// Convenience wrapper
// ---------------------------------------------------------------------------

export function CustomFieldRenderer({ field, ...rest }: FieldRendererProps) {
  const Renderer = FIELD_RENDERERS[field.type];
  if (!Renderer) {
    return (
      <Text size="sm" variant="muted">
        Unsupported field type
      </Text>
    );
  }
  return <Renderer field={field} {...rest} />;
}
